import React, { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from 'recharts';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { stripeService } from '../../services/stripe';

/**
 * Landing marketing — Planification d'investissement (module /investment-planning).
 * Route publique : /planification
 */
const PlanningLandingPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { theme, toggleTheme } = useTheme();
  const { user, userProfile } = useAuth();
  const tier = stripeService.normalizeTier(userProfile?.subscription_tier);
  const isPro = tier === 'pro';

  /** Illustration : parc ~5 M$, levier élevé, phase de décaissement à partir de l’an 10 */
  const chartData = useMemo(
    () => [
      { year: 0, brut: 5120000, dettes: 3240000, net: 1880000, tresorerieNetteAnnuelle: 58000 },
      { year: 2, brut: 5240000, dettes: 3310000, net: 1930000, tresorerieNetteAnnuelle: 67000 },
      { year: 4, brut: 5360000, dettes: 3360000, net: 2000000, tresorerieNetteAnnuelle: 74000 },
      { year: 6, brut: 5510000, dettes: 3410000, net: 2100000, tresorerieNetteAnnuelle: 82000 },
      { year: 8, brut: 5650000, dettes: 3460000, net: 2190000, tresorerieNetteAnnuelle: 91000 },
      { year: 10, brut: 5780000, dettes: 3480000, net: 2300000, tresorerieNetteAnnuelle: 98000 },
      { year: 12, brut: 5820000, dettes: 3420000, net: 2400000, tresorerieNetteAnnuelle: 104000 },
      { year: 14, brut: 5350000, dettes: 2850000, net: 2500000, tresorerieNetteAnnuelle: 138000 },
      { year: 16, brut: 5080000, dettes: 2480000, net: 2600000, tresorerieNetteAnnuelle: 152000 },
      { year: 18, brut: 4880000, dettes: 2120000, net: 2760000, tresorerieNetteAnnuelle: 168000 },
      { year: 20, brut: 4680000, dettes: 1780000, net: 2900000, tresorerieNetteAnnuelle: 176000 },
      { year: 22, brut: 4480000, dettes: 1520000, net: 2960000, tresorerieNetteAnnuelle: 182000 },
      { year: 25, brut: 4250000, dettes: 1320000, net: 2930000, tresorerieNetteAnnuelle: 188000 },
    ],
    [],
  );

  const scenarioDemo = useMemo(
    () => ({
      badge: 'Exemple illustratif · pas un dossier réel',
      name: 'Marc D.',
      age: 54,
      region: 'Rive-Nord, QC',
      headline: 'Parc immobilier à revenus — plan de décaissement avec levier maximal',
      composition: '5 immeubles (plex + quadruplex + local mixte) · 38 portes',
      valeurBrute: 5120000,
      dettesHypothecaires: 3240000,
      debutDecaissementAn: 10,
      strategie:
        'Scénario modélisé : maintien d’un levier élevé pendant la croissance (refinancements ciblés), puis ventes échelonnées et remboursement graduel du passif pour étaler les gains imposables et augmenter la trésorerie nette disponible à la retraite.',
    }),
    [],
  );

  const ratioLevier = Math.round((scenarioDemo.dettesHypothecaires / scenarioDemo.valeurBrute) * 100);

  const scenarioTimeline = useMemo(
    () => [
      {
        an: '+0',
        titre: 'Bilan initial',
        detail: '5 immeubles consolidés · ~5,1 M$ brut · dettes ~3,2 M$ pour garder le levier au sommet.',
      },
      {
        an: '+4 ans',
        titre: 'Croissance « full levier »',
        detail: 'Rehausse de loyers + refinancement ciblé : la dette suit la valeur, le capital net grimpe en douceur.',
      },
      {
        an: '+10 ans',
        titre: 'Pivot décaissement',
        detail: 'Objectif retraite : on arrête d’optimiser seulement la croissance et on planifie les sorties d’actifs.',
      },
      {
        an: '+14 ans',
        titre: '1re vente échelonnée',
        detail: 'Désinvestissement partiel : baisse du brut, baisse plus rapide des dettes, pic de trésorerie nette après impôt.',
      },
      {
        an: '+25 ans',
        titre: 'Patrimoine net stabilisé',
        detail: 'Moins d’immeubles, moins de passif, plus de liquidité annuelle — le graphique du haut est issu de cette logique.',
      },
    ],
    [],
  );

  const formatAxisMillions = (v) => {
    if (v == null || Number.isNaN(v)) return '';
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
    if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)} k`;
    return String(v);
  };

  const pillars = useMemo(
    () => [
      {
        icon: 'Percent',
        title: 'Leviers fiscaux sur la croissance',
        text: 'Visualise comment la fiscalité modèle ta trajectoire : revenus, gains, réinvestissement — pour prioriser ce qui garde le plus de dollars dans ton patrimoine.',
      },
      {
        icon: 'Hourglass',
        title: 'Décaissement sans angles morts',
        text: 'Anticipe la phase où tu consommes ton patrimoine : retraites, ventes, transmission. Compare des chemins pour réduire les surprises d’impôt au mauvais moment.',
      },
      {
        icon: 'Scale',
        title: 'Scénarios « avant / après » fiscalité',
        text: 'Duplique des hypothèses et confronte-les côte à côte : même ambition, fiscalité différente — le contraste devient lisible, pas enterré dans un classeur.',
      },
      {
        icon: 'TrendingUp',
        title: 'Croissance nette, pas seulement brute',
        text: 'Relie immobilier, placements et dettes à une vision après impôt sur l’horizon qui compte pour toi.',
      },
      {
        icon: 'Landmark',
        title: 'Cadre Québec & CAD',
        text: 'Une planification qui parle la même langue que ton comptable et tes relevés — conçue pour le contexte québécois.',
      },
      {
        icon: 'Sparkles',
        title: 'Événements qui bougent la ligne fiscale',
        text: 'Achats, ventes, refinancement, rénovations : positionne-les dans le temps pour voir l’effet cumulé sur trésorerie et impôt.',
      },
    ],
    [],
  );

  const steps = useMemo(
    () => [
      {
        n: '1',
        title: 'Cartographie patrimoniale',
        body: 'Structure entités, actifs, dettes et revenus — la toile de fond pour toute discussion sur optimisation fiscale et timing.',
      },
      {
        n: '2',
        title: 'Simuler croissance & décaissement',
        body: 'Enchaîne événements et scénarios sur plusieurs années : où la fiscalité accélère, où elle freine, où la transition se joue.',
      },
      {
        n: '3',
        title: 'Décider avec la trajectoire après impôt',
        body: 'Graphiques et comparaisons pour arbitrer entre stratégies — du tableau « beau sur le papier » au plan qui tient la route fiscalement.',
      },
    ],
    [],
  );

  const sansAvec = useMemo(
    () => ({
      sans: [
        'Tu optimises immeuble par immeuble, sans voir l’effet cumulé sur l’impôt dans 10 ou 20 ans.',
        'Refinancements et ventes restent des « one-offs » au lieu d’une séquence lisible.',
        'Tu navigues à vue : ton comptable voit le passé, toi tu ne vois pas la trajectoire nette après impôt.',
      ],
      avec: [
        'Bilan unifié + projections longues + scénarios comparables sur les mêmes données.',
        'Événements datés (ventes, refinancement, décaissement) et impact sur trésorerie & fiscalité.',
        'Tu négocies avec toi-même à partir de courbes — pas seulement à partir de cellules Excel.',
      ],
    }),
    [],
  );

  const handlePrimaryCta = () => {
    if (user?.id && isPro) {
      navigate('/investment-planning');
      return;
    }
    navigate('/subscription-pricing');
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background relative">
      <header className="sticky top-0 z-50 px-6 py-3.5 flex justify-between items-center max-w-7xl mx-auto w-full border-b border-border/80 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
        <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <img src="/immoiq-logo.png" alt="ImmoIQ" style={{ height: '36px', width: 'auto' }} />
          <span className="text-2xl font-bold text-foreground">ImmoIQ</span>
        </Link>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-transparent hover:border-border flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            aria-label={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
          >
            <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} />
            <span className="hidden sm:inline">{theme === 'dark' ? 'Clair' : 'Sombre'}</span>
          </button>

          <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
            Se connecter
          </Button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.38] dark:opacity-[0.22]"
          aria-hidden
          style={{
            background:
              'radial-gradient(ellipse 90% 55% at 50% -25%, var(--color-primary), transparent 55%), radial-gradient(ellipse 55% 45% at 100% 0%, var(--color-accent), transparent 50%), radial-gradient(ellipse 50% 40% at 0% 80%, var(--color-primary), transparent 45%)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.09]"
          aria-hidden
          style={{
            backgroundImage:
              'linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 75% 65% at 50% 35%, black 20%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 75% 65% at 50% 35%, black 20%, transparent 70%)',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 py-14 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
          <div className="animate-in fade-in slide-in-from-left-4 duration-500">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full">
                Module Pro
              </span>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Quand le patrimoine devient sérieux, la feuille Excel ne suffit plus
              </span>
            </div>
            <h1
              className="text-4xl md:text-5xl lg:text-[3.35rem] font-extrabold leading-[1.08] text-foreground mb-5 tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              La fiscalité de ta{' '}
              <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
                croissance
              </span>
              {' '}et de ton{' '}
              <span className="bg-gradient-to-r from-accent via-primary to-primary bg-clip-text text-transparent">
                décaissement
              </span>
              , sur une seule carte.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-5">
              Au-delà d’une analyse d’immeuble : une trajectoire <span className="text-foreground font-medium">après impôt</span>, des
              événements datés et des scénarios comparables — pour que tes gros coups de levier ne deviennent pas de gros coups dans l’eau
              dans dix ans.
            </p>
            <div className="rounded-xl border border-border/80 bg-card/60 backdrop-blur-sm p-4 mb-6 space-y-2.5">
              {[
                { icon: 'Target', text: 'Voir l’effet cumulé du fisc sur 25 ans, pas seulement l’année en cours.' },
                { icon: 'GitCompare', text: 'Comparer deux stratégies avec les mêmes chiffres de départ — le contraste saute aux yeux.' },
                { icon: 'Shield', text: 'Anticiper le décaissement avant que Revenu Québec devienne ton principal « conseiller ».' },
              ].map((row) => (
                <div key={row.text} className="flex gap-3 text-sm text-foreground/95">
                  <Icon name={row.icon} size={18} className="text-primary shrink-0 mt-0.5" />
                  <span className="leading-snug">{row.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8 pl-1 border-l-2 border-accent/60">
              Outil d’aide à la décision — ne remplace pas un avis fiscal ou financier personnalisé.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button
                variant="primary"
                size="lg"
                iconName={user?.id && isPro ? 'LayoutDashboard' : 'Crown'}
                iconPosition="left"
                onClick={handlePrimaryCta}
                className="h-12 px-6 rounded-xl shadow-lg shadow-primary/20 justify-center hover:shadow-xl hover:shadow-primary/25 transition-shadow ring-offset-2 ring-offset-background hover:ring-2 hover:ring-primary/30"
              >
                {user?.id && isPro ? 'Ouvrir la planification' : 'Voir ImmoIQ Pro'}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/register')}
                className="h-12 px-6 rounded-xl justify-center"
              >
                Créer un compte
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mb-8">
              Inclus avec l’abonnement{' '}
              <span className="text-foreground font-semibold">Pro</span>.{' '}
              {!user?.id && (
                <>
                  <button
                    type="button"
                    className="text-primary underline-offset-2 hover:underline font-medium"
                    onClick={() => navigate('/login')}
                  >
                    Déjà client ?
                  </button>{' '}
                  Connecte-toi puis ouvre l’outil depuis le tableau de bord.
                </>
              )}
            </p>

            <div className="grid grid-cols-3 gap-3 pt-6 border-t border-border">
              {[
                { k: '25 ans', s: 'Horizon', sub: 'Projections longues' },
                { k: '3', s: 'vues', sub: 'Bilan · Planif. · Analyse' },
                { k: '∞', s: 'scénarios', sub: 'Comparer sans limite' },
              ].map((cell) => (
                <div
                  key={cell.s}
                  className="rounded-xl border border-border/80 bg-gradient-to-b from-card to-muted/20 px-2 py-3 sm:px-3 text-center shadow-sm"
                >
                  <div className="text-xl sm:text-2xl font-extrabold text-foreground tabular-nums tracking-tight">{cell.k}</div>
                  <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-primary mt-0.5">{cell.s}</div>
                  <div className="text-[10px] sm:text-xs text-muted-foreground mt-1 leading-tight">{cell.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative animate-in fade-in slide-in-from-right-4 duration-500 delay-100">
            <div
              className="absolute -inset-px rounded-2xl bg-gradient-to-br from-primary/45 via-accent/25 to-primary/10 opacity-90 blur-[1px]"
              aria-hidden
            />
            <div className="relative rounded-2xl border border-border bg-card shadow-2xl shadow-primary/10 overflow-hidden ring-1 ring-white/10 dark:ring-white/5">
              <div className="px-4 py-3 sm:px-5 border-b border-border flex flex-wrap items-center justify-between gap-2 bg-muted/30">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon name="LayoutPanelLeft" size={20} className="text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">Aperçu planification Pro</div>
                    <div className="text-xs text-muted-foreground truncate">Même logique que dans l’app — chiffres d’exemple</div>
                  </div>
                </div>
                <span className="text-xs font-medium px-2 py-1 rounded-md bg-amber-500/15 text-amber-800 dark:text-amber-200 shrink-0">
                  {scenarioDemo.badge}
                </span>
              </div>

              <div className="p-4 sm:p-5 border-b border-border bg-background/40 space-y-3">
                <div className="flex gap-3">
                <div
                  className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center text-sm font-bold text-primary shrink-0"
                  aria-hidden
                >
                  MD
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="font-semibold text-foreground">{scenarioDemo.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {scenarioDemo.age} ans · {scenarioDemo.region}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground mt-1 leading-snug">{scenarioDemo.headline}</p>
                  <p className="text-xs text-muted-foreground mt-1">{scenarioDemo.composition}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-muted text-foreground border border-border">
                      Levier ~{ratioLevier} %
                    </span>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-muted text-foreground border border-border">
                      Décaissement modélisé : +{scenarioDemo.debutDecaissementAn} ans
                    </span>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-muted text-foreground border border-border">
                      Horizon 25 ans
                    </span>
                  </div>
                </div>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <div className="rounded-lg border border-border bg-card px-2 py-2 sm:px-3 sm:py-2.5">
                    <div className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">
                      Valeur brute
                    </div>
                    <div className="text-sm sm:text-base font-bold text-foreground tabular-nums leading-tight mt-0.5">
                      {formatCurrency(scenarioDemo.valeurBrute)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-card px-2 py-2 sm:px-3 sm:py-2.5">
                    <div className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">
                      Dettes
                    </div>
                    <div className="text-sm sm:text-base font-bold text-foreground tabular-nums leading-tight mt-0.5">
                      {formatCurrency(scenarioDemo.dettesHypothecaires)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-card px-2 py-2 sm:px-3 sm:py-2.5">
                    <div className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">
                      Capital net
                    </div>
                    <div className="text-sm sm:text-base font-bold text-primary tabular-nums leading-tight mt-0.5">
                      {formatCurrency(scenarioDemo.valeurBrute - scenarioDemo.dettesHypothecaires)}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-3">
                  {scenarioDemo.strategie}
                </p>
              </div>

              <div className="p-4 sm:p-5">
                <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-foreground">Trajectoire 25 ans (illustration)</span>
                <span className="text-[10px] text-muted-foreground hidden sm:inline">
                  Axe gauche : patrimoine · droite : trés. nette / an
                </span>
                </div>
                <div className="h-[300px] w-full min-h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 8, right: 6, left: 0, bottom: 4 }}>
                      <defs>
                        <linearGradient id="plNet" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="plDette" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="year" tick={{ fontSize: 10 }} tickFormatter={(y) => `+${y}a`} />
                      <YAxis
                        yAxisId="left"
                        width={44}
                        tick={{ fontSize: 10 }}
                        tickFormatter={formatAxisMillions}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        width={40}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 12,
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          maxWidth: 280,
                        }}
                        formatter={(value, name) => {
                          const labels = {
                            net: 'Capital net',
                            brut: 'Valeur brute immeubles',
                            dettes: 'Dettes (levier)',
                            tresorerieNetteAnnuelle: 'Trésorerie nette / an (estim.)',
                          };
                          return [formatCurrency(value), labels[name] || name];
                        }}
                        labelFormatter={(label) => `Depuis aujourd’hui : +${label} ans`}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        formatter={(value) => {
                          const m = {
                            brut: 'Valeur brute',
                            dettes: 'Dettes',
                            net: 'Capital net',
                            tresorerieNetteAnnuelle: 'Trésor. nette / an',
                          };
                          return m[value] || value;
                        }}
                      />
                      <ReferenceLine
                        x={scenarioDemo.debutDecaissementAn}
                        yAxisId="left"
                        stroke="var(--color-muted-foreground)"
                        strokeDasharray="5 5"
                        label={{
                          value: 'Début décaissement modélisé',
                          position: 'top',
                          fill: 'var(--color-muted-foreground)',
                          fontSize: 10,
                        }}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="brut"
                        stroke="#94a3b8"
                        fill="#94a3b8"
                        fillOpacity={0.06}
                        strokeWidth={1}
                        name="brut"
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="dettes"
                        stroke="var(--color-accent)"
                        fill="url(#plDette)"
                        strokeWidth={2}
                        name="dettes"
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="net"
                        stroke="var(--color-primary)"
                        fill="url(#plNet)"
                        strokeWidth={2}
                        name="net"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="tresorerieNetteAnnuelle"
                        stroke="#059669"
                        strokeWidth={2.5}
                        dot={false}
                        name="tresorerieNetteAnnuelle"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                  Dans ImmoIQ Pro, tu saisis ce bilan dans l’onglet <span className="text-foreground font-medium">Bilan</span>, tu
                  positionnes ventes et refinancement dans <span className="text-foreground font-medium">Planification</span>, puis tu
                  compares des scénarios dans <span className="text-foreground font-medium">Analyse</span> — avec les mêmes types de
                  graphiques sur tes données.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-gradient-to-b from-muted/40 via-muted/20 to-background">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
              Pourquoi c’est devenu <span className="text-primary">indispensable</span>
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-2">
              Dès que plusieurs immeubles, du levier et une date de retraite entrent en jeu, le risque n’est plus « mauvais achat » :
              c’est le <span className="text-foreground font-medium">coût fiscal du désordre</span> sur 15–25 ans.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <div className="rounded-2xl border border-border bg-card/80 p-6 md:p-7 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <Icon name="XCircle" size={18} className="text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-foreground">Sans planification structurée</h3>
              </div>
              <ul className="space-y-3">
                {sansAvec.sans.map((line) => (
                  <li key={line} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                    <span className="text-destructive shrink-0 font-bold mt-0.5" aria-hidden>
                      ·
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-primary/35 bg-gradient-to-br from-primary/[0.07] via-card to-accent/[0.06] p-6 md:p-7 shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/15 transition-shadow duration-300 relative overflow-hidden">
              <div
                className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/15 blur-2xl"
                aria-hidden
              />
              <div className="flex items-center gap-2 mb-4 relative">
                <div className="h-9 w-9 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Icon name="CheckCircle2" size={18} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">Avec ImmoIQ Pro</h3>
              </div>
              <ul className="space-y-3 relative">
                {sansAvec.avec.map((line) => (
                  <li key={line} className="flex gap-3 text-sm text-foreground/95 leading-relaxed">
                    <Icon name="Check" size={16} className="text-primary shrink-0 mt-0.5" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-muted/25">
        <div className="max-w-7xl mx-auto px-6 py-10 md:py-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
                Chronologie du même scénario (exemple)
              </h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                En planification, tu encodes ce genre d’étapes comme événements — la courbe du graphique en découle.
              </p>
            </div>
            <span className="text-xs text-muted-foreground shrink-0">{scenarioDemo.badge}</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory md:grid md:grid-cols-5 md:overflow-visible">
            {scenarioTimeline.map((m) => (
              <div
                key={m.titre}
                className="snap-start shrink-0 w-[min(100%,220px)] md:w-auto rounded-xl border border-border bg-card p-4 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-primary/35 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md">{m.an}</span>
                </div>
                <h3 className="text-sm font-semibold text-foreground leading-tight">{m.titre}</h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">{m.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">Le module en bref</p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Une seule toile pour le fisc sur{' '}
            <span className="text-primary">tout le cycle de vie</span>
          </h2>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto mt-4 text-base md:text-lg leading-relaxed">
            De l’accumulation au décaissement : chaque décision — refinancement, vente, transmission — laisse une trace sur
            ta ligne après impôt. Ici, tu la vois d’un coup d’œil.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p) => (
            <div
              key={p.title}
              className="group rounded-xl border border-border bg-card p-5 md:p-6 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-0.5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-3 group-hover:bg-primary/15 transition-colors">
                <Icon name={p.icon} size={20} />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
            Du bilan à la stratégie fiscale sur le long terme
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.n} className="text-center md:text-left">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm mb-4">
                  {s.n}
                </div>
                <h3 className="font-semibold text-foreground text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/[0.08] via-card to-accent/[0.06] p-8 md:p-12 lg:p-14 shadow-2xl shadow-primary/10">
          <div
            className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl"
            aria-hidden
          />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-12">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">Écosystème ImmoIQ</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                L’analyse d’un immeuble est l’entrée.
                <br />
                <span className="text-primary">La planification en est le sommet.</span>
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                Tu sais déjà si un plex « tient la route ». Le module Pro répond à la suite logique : comment il s’insère dans
                ta croissance, ton impôt cumulé et ton décaissement — pour que tu investisses avec une carte, pas avec un
                carnet de notes.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full lg:w-auto">
              <Button variant="outline" size="lg" className="rounded-xl h-12 border-border/80 bg-background/80 backdrop-blur-sm" onClick={() => navigate('/')}>
                Analyser un immeuble
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="rounded-xl h-12 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow ring-offset-2 ring-offset-card hover:ring-2 hover:ring-primary/35"
                onClick={handlePrimaryCta}
              >
                {user?.id && isPro ? 'Ouvrir la planification' : 'Passer à Pro'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="px-6 py-10 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} ImmoIQ — planification patrimoniale & fiscale sur le long terme.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/terms')}>
              Conditions
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/privacy')}>
              Confidentialité
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/subscription-pricing')}>
              Forfaits
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlanningLandingPage;
